/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createApiRef } from '@backstage/core-plugin-api';

export type AdrFileInfo = {
  /** File metadata */
  type: string;
  path: string;
  name: string;

  /** Core ADR metadata */
  title?: string;
  status?: string;
  date?: string;

  /** Full ADR content fields */
  description?: string;
  objectives?: string;
  deciders?: string;
  decision?: string;
  recommendations?: string;
  consequences?: string;

  /** Platform chosen for this ADR (`- Platform:`), not the Platform Impacted section. */
  platform?: string;
  /** Division chosen for this ADR (`- Division:`). */
  division?: string;
  platformImpacted?: string;
  technologyCapabilities?: string;
  techStack?: string;

  councilMember?: string;
  majorFeatures?: string;

  referenceLinks?: string[];
  referenceFiles?: string[];
  referenceImages?: string[];
};

export type AdrListResult = {
  data: AdrFileInfo[];
};

export type AdrReadResult = {
  /** The contents of the read ADR file. */
  data: string;
};

export interface AdrApi {
  /**
   * Lists the ADRs at the provided url. Only file metadata is returned, so this
   * stays fast even when the backend cache is cold.
   */
  listAdrs(url: string): Promise<AdrListResult>;

  /** Lists the ADRs at the provided url including their parsed MADR fields. */
  listAdrMetadata(url: string): Promise<AdrListResult>;

  /** Reads the contents of the ADR at the provided url. */
  readAdr(url: string): Promise<AdrReadResult>;
}

export const adrApiRef = createApiRef<AdrApi>({
  id: 'plugin.adr.api',
});
